/**
 * Componente de Alerta
 * Exibe alertas e mensagens para o usuário com diferentes variantes
 */

import { ReactNode } from 'react'

interface AlertProps {
  variant?: 'error' | 'success' | 'warning' | 'info'
  children: ReactNode
  className?: string
}

const variantStyles = {
  error: {
    container: 'bg-red-50 ring-1 ring-red-200',
    icon: 'text-red-600',
    text: 'text-red-800',
  },
  success: {
    container: 'bg-green-50 ring-1 ring-green-200',
    icon: 'text-green-600',
    text: 'text-green-800',
  },
  warning: {
    container: 'bg-yellow-50 ring-1 ring-yellow-200',
    icon: 'text-yellow-600',
    text: 'text-yellow-800',
  },
  info: {
    container: 'bg-blue-50 ring-1 ring-blue-200',
    icon: 'text-blue-600',
    text: 'text-blue-800',
  },
}

const icons = {
  error: (
    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
    </svg>
  ),
  success: (
    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
    </svg>
  ),
  warning: (
    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
    </svg>
  ),
  info: (
    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
    </svg>
  ),
}

export function Alert({ variant = 'info', children, className = '' }: AlertProps) {
  const styles = variantStyles[variant]

  return (
    <div className={`rounded-lg p-3 ${styles.container} ${className}`}>
      <div className="flex items-start">
        <div className={`flex-shrink-0 ${styles.icon}`}>
          {icons[variant]}
        </div>
        <div className={`ml-3 flex-1 text-sm ${styles.text}`}>
          {children}
        </div>
      </div>
    </div>
  )
}
